import React, { useRef, useState } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { Camera, XCircleFill, ZoomIn, X } from 'react-bootstrap-icons';
import Swal from 'sweetalert2';
import '../../assets/css/vendors/HotelComponents/hotelModal.css';

const AddRoom = ({ 
  showModal, 
  setShowModal, 
  editingItem, 
  formData, 
  setFormData, 
  handleSave,
  loading 
}) => {
  const fileInputRef = useRef(null);
  const [zoomedImg, setZoomedImg] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const currentCount = (formData.images?.length || 0) + (formData.existing_images?.length || 0);
    const remaining = 3 - currentCount;
    
    if (remaining <= 0) {
      Swal.fire('Limit Reached', 'Maximum 3 images allowed', 'warning');
      return;
    }

    const filesToProcess = files.slice(0, remaining);
    let loaded = 0;
    const newPreviews = [...(formData.images || [])];
    const newFiles = [...(formData.imageFiles || [])];

    filesToProcess.forEach(file => {
      if (!file.type.startsWith('image/')) {
        Swal.fire('Invalid File', `${file.name} is not an image file`, 'error');
        return;
      }
      
      if (file.size > 2 * 1024 * 1024) {
        Swal.fire('File Too Large', `${file.name} is too large. Maximum size is 2MB`, 'error');
        return;
      }
      
      newFiles.push(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        loaded++;
        if (loaded === filesToProcess.length) {
          setFormData(prev => ({
            ...prev,
            images: newPreviews,
            imageFiles: newFiles
          }));
        }
      };
      reader.readAsDataURL(file);
    });

    e.target.value = '';
  };

  const handleRemoveNewImage = (index) => {
    const updatedPreviews = [...(formData.images || [])];
    const updatedFiles = [...(formData.imageFiles || [])];
    updatedPreviews.splice(index, 1);
    updatedFiles.splice(index, 1);
    setFormData(prev => ({
      ...prev,
      images: updatedPreviews,
      imageFiles: updatedFiles
    }));
  };

  const handleRemoveExistingImage = (imageId, index) => {
    const updatedExisting = [...(formData.existing_images || [])];
    updatedExisting.splice(index, 1);
    setFormData(prev => ({
      ...prev,
      existing_images: updatedExisting,
      deleted_image_ids: [...(prev.deleted_image_ids || []), imageId]
    }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.room_number || formData.room_number.trim() === '') {
      Swal.fire('Validation Error', 'Room Number is required', 'warning');
      return;
    }
    if (!formData.room_type) {
      Swal.fire('Validation Error', 'Room Type is required', 'warning');
      return;
    }
    if (!formData.price || formData.price <= 0) {
      Swal.fire('Validation Error', 'Valid Price is required', 'warning');
      return;
    }
    if (!formData.capacity || formData.capacity <= 0) {
      Swal.fire('Validation Error', 'Valid Capacity is required', 'warning');
      return;
    }
    if (!formData.floor || formData.floor <= 0) {
      Swal.fire('Validation Error', 'Valid Floor number is required', 'warning');
      return;
    }
    
    handleSave(e);
  };

  const images = formData.images || [];
  const existingImages = formData.existing_images || [];
  const allImages = [
    ...existingImages.map((img, idx) => ({ type: 'existing', id: img.id, url: img.url, index: idx })),
    ...images.map((url, idx) => ({ type: 'new', url: url, index: idx }))
  ];

  const amenitiesValue = Array.isArray(formData.amenities) 
    ? formData.amenities.join(', ') 
    : (formData.amenities || '');

  return (
    <>
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered className="custom-modal">
        <Form onSubmit={onSubmit}>
          <Modal.Header closeButton className="modal-header-custom">
            <Modal.Title className="hotelmodal-title">
              {editingItem ? 'Edit' : 'Add'} Room
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="modal-body-custom">
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Room Number <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    type="text"
                    name="room_number" 
                    value={formData.room_number || ''} 
                    onChange={handleInputChange} 
                    required 
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Room Type <span className="text-danger">*</span></Form.Label>
                  <Form.Select 
                    name="room_type" 
                    value={formData.room_type || ''} 
                    onChange={handleInputChange} 
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="Standard">Standard</option>
                    <option value="Deluxe">Deluxe</option>
                    <option value="Suite">Suite</option>
                    <option value="Presidential">Presidential</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Room Images */}
            <Form.Group className="mb-3">
              <Form.Label>
                Room Images <span className="text-muted" style={{ fontSize: '0.8rem' }}>(Max 3)</span>
              </Form.Label>
              <div className="room-images-row">
                {allImages.map((img, idx) => (
                  <div key={idx} className="room-image-thumb-wrap">
                    <img 
                      src={img.url} 
                      alt={`Room ${idx + 1}`} 
                      className="room-image-thumb" 
                      onClick={() => setZoomedImg(img.url)} 
                    />
                    <div className="room-image-overlay" onClick={() => setZoomedImg(img.url)}>
                      <ZoomIn size={16} color="white" />
                    </div>
                    <button 
                      type="button" 
                      className="room-image-remove" 
                      onClick={() => img.type === 'existing' 
                        ? handleRemoveExistingImage(img.id, img.index)
                        : handleRemoveNewImage(img.index)
                      }
                    >
                      <XCircleFill size={17} />
                    </button>
                  </div>
                ))}
                {allImages.length < 3 && (
                  <div className="room-image-add-slot" onClick={() => fileInputRef.current.click()}>
                    <Camera size={22} className="room-image-add-icon" />
                    <span>Add Photo</span>
                    <input 
                      ref={fileInputRef} 
                      type="file" 
                      accept="image/*" 
                      multiple 
                      style={{ display: 'none' }} 
                      onChange={handleImageUpload} 
                    />
                  </div>
                )}
              </div>
              <Form.Text className="text-muted">Upload up to 3 photos (JPG, PNG - Max 2MB each). Click image to zoom.</Form.Text>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Price (Rs.) <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    type="number" 
                    name="price"
                    value={formData.price || ''} 
                    onChange={handleInputChange} 
                    required 
                    min="0" 
                    step="1"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Capacity (Persons) <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    type="number" 
                    name="capacity" 
                    value={formData.capacity || ''} 
                    onChange={handleInputChange} 
                    required 
                    min="1" 
                    step="1"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Floor <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    type="number" 
                    name="floor" 
                    value={formData.floor || ''} 
                    onChange={handleInputChange} 
                    required 
                    min="1" 
                    step="1"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status <span className="text-danger">*</span></Form.Label>
                  <Form.Select 
                    name="status" 
                    value={formData.status || 'Available'} 
                    onChange={handleInputChange} 
                    required
                  >
                    <option value="Available">Available</option>
                    <option value="Booked">Booked</option>
                    <option value="Maintenance">Maintenance</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Amenities</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={2} 
                name="amenities" 
                value={amenitiesValue}
                onChange={handleInputChange} 
                placeholder="WiFi, AC, TV, Minibar"
              />
              <Form.Text className="text-muted">Separate amenities with commas (e.g., WiFi, AC, TV)</Form.Text>
            </Form.Group>
          </Modal.Body>

          <Modal.Footer className="modal-footer-custom">
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Saving...' : (editingItem ? 'Update' : 'Save')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {zoomedImg && (
        <div className="rc-zoom-backdrop" onClick={() => setZoomedImg(null)}>
          <div className="rc-zoom-box" onClick={(e) => e.stopPropagation()}>
            <button className="rc-zoom-close" onClick={() => setZoomedImg(null)}>
              <X size={22} />
            </button>
            <img src={zoomedImg} alt="Zoomed" className="rc-zoom-img" />
          </div>
        </div>
      )}
    </>
  );
};

export default AddRoom;